import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  AgentTrackingNumberUsageCategory,
  TrackingNumberDetail,
  TrackingNumberStatus,
} from '../../../interfaces/calls';

type Props = {
  open: boolean;
  trackingNumber: TrackingNumberDetail | null;
  onClose: () => void;
  onEdit: () => void;
};

const STATUS_COLORS = {
  [TrackingNumberStatus.ACTIVE]: '#4caf50',
  [TrackingNumberStatus.INACTIVE]: '#9e9e9e',
};

const STATUS_DISPLAY = {
  [TrackingNumberStatus.ACTIVE]: 'Active',
  [TrackingNumberStatus.INACTIVE]: 'Inactive',
};

const USAGE_CATEGORY_DISPLAY = {
  [AgentTrackingNumberUsageCategory.INCOMING]: 'Incoming',
  [AgentTrackingNumberUsageCategory.OUTGOING]: 'Outgoing',
  [AgentTrackingNumberUsageCategory.SMS]: 'SMS',
};

const USAGE_CATEGORY_COLORS = {
  [AgentTrackingNumberUsageCategory.INCOMING]: '#2196f3',
  [AgentTrackingNumberUsageCategory.OUTGOING]: '#4caf50',
  [AgentTrackingNumberUsageCategory.SMS]: '#ff9800',
};

export const TrackingNumberDetailModal = ({ open, trackingNumber, onClose, onEdit }: Props) => {
  if (!trackingNumber) {
    return null;
  }

  const getStatusColor = (status: TrackingNumberStatus) => {
    return STATUS_COLORS[status] || '#757575';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      sx={{
        '& .MuiDialog-container > .MuiPaper-elevation': {
          borderRadius: '12px',
          maxHeight: '90vh',
          maxWidth: '50vw',
        },
      }}>
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        }}>
        {/* Gray top bar with close button */}
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '4px 10px',
          }}>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Header with icon, title, and status chip */}
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          padding='0 24px'
          marginTop={2}>
          <Box display='flex' alignItems='center' gap={1}>
            <PhoneIcon sx={{ color: '#389FBA', fontSize: 20 }} />

            <Typography variant='h6' fontWeight={600}>
              Phone Number Details
            </Typography>
          </Box>

          <Box display='flex' gap={1}>
            <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={onEdit}
            >
                Edit
            </Button>
            <Chip
                label={STATUS_DISPLAY[trackingNumber.status]}
                size='small'
                sx={{
                backgroundColor: getStatusColor(trackingNumber.status),
                color: 'white',
                fontSize: '12px',
                }}
            />
          </Box>
        </Box>

        {/* Tracking Number Information */}
        <Box pt={3} pb={2} borderBottom='2px solid #e9ecef'>
          <Box display='flex' flexDirection='column' gap={1} pl={3} pr={3}>
            <Box display='flex' alignItems='center' gap={1}>
              <Typography variant='h5' fontWeight='bold'>
                {trackingNumber.trackingNumber}
              </Typography>
              {trackingNumber.isMainLine && (
                <Chip
                  size='small'
                  label='Main Line'
                  sx={{ fontSize: '11px', backgroundColor: '#2196f3', color: 'white' }}
                />
              )}
              {trackingNumber.isTollFreeLine && (
                <Chip
                  size='small'
                  label='Toll Free'
                  sx={{ fontSize: '11px', backgroundColor: '#9c27b0', color: 'white' }}
                />
              )}
            </Box>
            {trackingNumber.label && (
              <Typography variant='body2' color='textSecondary'>
                {trackingNumber.label}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        {/* Features Section */}
        <Box p={3} borderBottom='2px solid #e9ecef'>
          <Typography variant='subtitle1' fontWeight='bold' mb={2}>
            Features & Capabilities
          </Typography>

          <Box display='flex' flexDirection='column' gap={1.5}>
            {/* Voice Features */}
            <Box>
              <Typography variant='body2' color='textSecondary' fontWeight={600} mb={0.5}>
                Voice Calling
              </Typography>
              <Box display='flex' gap={0.5} flexWrap='wrap'>
                {trackingNumber.enabledForVoiceCalls ? (
                  <>
                    {trackingNumber.enabledForInboundCalls && (
                      <Chip
                        size='small'
                        label='Inbound'
                        variant='outlined'
                        sx={{ fontSize: '11px' }}
                      />
                    )}
                    {trackingNumber.enabledForOutboundCalls && (
                      <Chip
                        size='small'
                        label='Outbound'
                        variant='outlined'
                        sx={{ fontSize: '11px' }}
                      />
                    )}
                    {trackingNumber.enabledForVoicemail && (
                      <Chip
                        size='small'
                        label='Voicemail'
                        variant='outlined'
                        sx={{ fontSize: '11px' }}
                      />
                    )}
                    {trackingNumber.maxConcurrentCalls && (
                      <Chip
                        size='small'
                        label={`Max ${trackingNumber.maxConcurrentCalls} concurrent calls`}
                        variant='outlined'
                        sx={{ fontSize: '11px' }}
                      />
                    )}
                  </>
                ) : (
                  <Typography variant='body2' color='textSecondary'>
                    Voice calls disabled
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Other Features */}
            <Box>
              <Typography variant='body2' color='textSecondary' fontWeight={600} mb={0.5}>
                Other Features
              </Typography>
              <Box display='flex' gap={0.5} flexWrap='wrap'>
                {trackingNumber.enabledForReminders && (
                  <Chip
                    size='small'
                    label='Reminders'
                    variant='outlined'
                    sx={{ fontSize: '11px' }}
                  />
                )}
                {!trackingNumber.enabledForReminders && (
                  <Typography variant='body2' color='textSecondary'>
                    No additional features enabled
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Call Route */}
            {trackingNumber.callRouteId && (
              <Box>
                <Typography variant='body2' color='textSecondary' fontWeight={600} mb={0.5}>
                  Call Route
                </Typography>
                <Typography variant='body2'>{trackingNumber.callRouteName || 'Unknown'}</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Agent Assignments Section */}
        <Box p={3}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
            <Typography variant='subtitle1' fontWeight='bold'>
              Agent Assignments
            </Typography>
            <Box display='flex' gap={1}>
              <Chip
                size='small'
                label={`${trackingNumber.incomingAgentsCount} Incoming`}
                sx={{ fontSize: '11px' }}
              />
              <Chip
                size='small'
                label={`${trackingNumber.outgoingAgentsCount} Outgoing`}
                sx={{ fontSize: '11px' }}
              />
              <Chip
                size='small'
                label={`${trackingNumber.smsAgentsCount} SMS`}
                sx={{ fontSize: '11px' }}
              />
            </Box>
          </Box>

          {trackingNumber.agentAssignments.length > 0 ? (
            <TableContainer sx={{ border: '1px solid #e9ecef', borderRadius: 1 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usage Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Primary</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trackingNumber.agentAssignments.map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell>
                        <Typography variant='body2'>
                          {assignment.agentName || `Agent #${assignment.agentId}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={USAGE_CATEGORY_DISPLAY[assignment.usageCategory]}
                          sx={{
                            fontSize: '11px',
                            backgroundColor: USAGE_CATEGORY_COLORS[assignment.usageCategory],
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{assignment.priority}</Typography>
                      </TableCell>
                      <TableCell>
                        {assignment.isPrimary && (
                          <Chip
                            size='small'
                            label='Primary'
                            sx={{ fontSize: '10px', backgroundColor: '#4caf50', color: 'white' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {assignment.agentStatus && (
                          <Typography variant='caption' color='textSecondary'>
                            {assignment.agentStatus.replace('_', ' ')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              p={3}
              textAlign='center'
              border='1px solid #e9ecef'
              borderRadius={1}
              bgcolor='#f8f9fa'>
              <Typography variant='body2' color='textSecondary'>
                No agents assigned to this tracking number
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
